import React, { useEffect, useState } from 'react'

import ShortId from 'shortid'

import { Condition, FilterType } from 'types/GlobalKpiOption'
import {
  isCustomEconomyMetric,
  ISelectMetric,
  TMetricCondition,
  TMetricFilterOptions,
  TMetricFilterType
} from 'types/GlobalMetric'

import { isOnlyLeftFilter } from 'utils/functions'

import Box from 'components_new/atoms/Box'
import Skeleton from 'components_new/atoms/Skeleton'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import FormSection from 'components_new/molecules/FormSection'
import ListItemButton from 'components_new/molecules/ListItemButton'

import FilterItem from 'components_new/organisms/FilterItem'

import { useMetricActions } from 'redux/actions/Metrics'
import { useMetric, useMetricFilters } from 'redux/hooks/Metrics'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { useTranslation } from 'language/translator'

interface FilterSectionProps {
  edit: boolean
  metricId: string
}

type TStateFilter = {
  id: string
  index: number
  attribute_option_id: string | null
  condition: TMetricCondition
  values: string[]
}
type TValidStateFilter = TStateFilter & { attribute_option_id: string }

interface IValidFilterBody {
  attribute_option_id: string
  type: TMetricFilterType
  condition: TMetricCondition
  right_value: string | null
}

const getInitFilters = (
  metric: ISelectMetric,
  filterOptions: TMetricFilterOptions
) => {
  const init: { [key: string]: TStateFilter } = {}

  const sortedFilters = [...metric.filters]
    .map((filter) => {
      const selectedOption = filter.attribute_option_id
        ? filterOptions[filter.attribute_option_id]
        : null

      return {
        ...filter,
        name: selectedOption?.name ?? null
      }
    })
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

  sortedFilters.forEach((filter, index) => {
    const localId = ShortId.generate()

    init[localId] = {
      id: localId,
      ...filter,
      index
    }
  })

  return init
}

const validateFilter = (filter: TStateFilter): filter is TValidStateFilter => {
  return (
    !!filter.attribute_option_id &&
    (isOnlyLeftFilter(filter.condition) || filter.values.length > 0)
  )
}

const FilterSection = (props: FilterSectionProps) => {
  const { edit, metricId } = props

  const { tryUpdateMetricSettings } = useMetricActions()
  const { t } = useTranslation()
  const metric = useMetric(metricId, null)
  const { data: filterOptions, loading: filterOptionsLoading } =
    useMetricFilters(metricId)

  const [filters, setFilters] = useState<{ [key: string]: TStateFilter }>({})

  useEffect(() => {
    if (metric) {
      setFilters(getInitFilters(metric, filterOptions))
    }
  }, [metric])

  const isNumericOption = (attributeOptionId: string | null) =>
    (attributeOptionId && filterOptions[attributeOptionId]?.numeric) ?? false

  // update kpi with all valid filters
  const putValidFilters = (newFilters: { [key: string]: TStateFilter }) => {
    if (!metric) return

    const validFilters = Object.values(newFilters)
      .filter(validateFilter)
      .flatMap<IValidFilterBody>((filter) => {
        if (isOnlyLeftFilter(filter.condition)) {
          return [
            {
              attribute_option_id: filter.attribute_option_id,
              type: FilterType.ONLY_LEFT,
              condition: filter.condition,
              right_value: null
            }
          ]
        }

        if (isNumericOption(filter.attribute_option_id)) {
          return [
            {
              attribute_option_id: filter.attribute_option_id,
              type: FilterType.NUMBER,
              condition: filter.condition,
              right_value: filter.values[0]
            }
          ]
        }

        return filter.values.map((value) => ({
          attribute_option_id: filter.attribute_option_id,
          type: FilterType.INPUT_VALUE,
          condition: filter.condition,
          right_value: value
        }))
      })

    tryUpdateMetricSettings(metric.id, { filters: validFilters })
  }

  const handleDeleteFilter = (filter: TStateFilter) => {
    const newFilters = { ...filters }

    const deletedFilter = { ...newFilters[filter.id] }

    delete newFilters[filter.id]

    setFilters(newFilters)
    if (validateFilter(deletedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleChangeCondition = (
    filter: TStateFilter,
    condition: TMetricCondition
  ) => {
    const changedFilter = {
      ...filters[filter.id],
      condition
    }

    if (isOnlyLeftFilter(condition)) {
      // clear values
      changedFilter.values = []
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)
    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleChangeAttribute = (
    filter: TStateFilter,
    attributeOptionId: string
  ) => {
    const changedFilter = {
      ...filters[filter.id],
      attribute_option_id: attributeOptionId
    }

    if (filters[filter.id].attribute_option_id !== attributeOptionId) {
      // changed attribute --> clear values
      changedFilter.values = []

      const numericConditions: TMetricCondition[] = [Condition.GT, Condition.LT]

      if (
        !isNumericOption(attributeOptionId) &&
        numericConditions.includes(changedFilter.condition)
      ) {
        // numeric-only condition on a value-list attribute --> reset
        changedFilter.condition = Condition.EQ
      }
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)

    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleChangeValues = (filter: TStateFilter, values: string[]) => {
    const changedFilter = {
      ...filters[filter.id],
      values
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)

    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const numberOfFilters = Object.keys(filters).length
  const isCustomEconomy = isCustomEconomyMetric(metric)

  return (
    <FormSection
      helperText={t('metrics', 'filters_description')}
      importance="recommended"
      title={t('metrics', 'filters_title')}
    >
      {metric && !filterOptionsLoading ? (
        <Box sx={{ bgcolor: 'background.default', borderRadius: 1, p: 1 }}>
          {numberOfFilters === 0 ? (
            <TooltipWrapper
              title={
                isCustomEconomy
                  ? t('metrics', 'custom_economy_availability_tooltip')
                  : undefined
              }
            >
              <span>
                <EmptyPlaceholder
                  background={true}
                  title={t('filters', 'no_filters')}
                  sx={{ ['&:not(:only-child)']: { mb: 1 } }}
                />
              </span>
            </TooltipWrapper>
          ) : (
            Object.values(filters)
              .sort((a, b) => a.index - b.index)
              .map((filter) => {
                const selectedOption = filter.attribute_option_id
                  ? filterOptions[filter.attribute_option_id]
                  : null

                const valueOptions = selectedOption
                  ? selectedOption.options.map((value) => ({
                    id: value,
                    title: value
                  }))
                  : []

                const options = Object.values(filterOptions).map((option) => ({
                  id: option.id,
                  title: option.name
                }))

                return (
                  <FilterItem
                    attributeOptions={options}
                    attributeSelected={
                      selectedOption
                        ? {
                            id: selectedOption.id,
                            title: selectedOption.name
                          }
                        : null
                    }
                    conditionSelected={filter.condition}
                    editable={edit}
                    key={filter.id}
                    numeric={selectedOption?.numeric ?? false}
                    onChangeAttribute={(columnId) =>
                      handleChangeAttribute(filter, columnId)
                    }
                    onChangeCondition={(condition) =>
                      handleChangeCondition(filter, condition)
                    }
                    onChangeValue={(values) =>
                      handleChangeValues(filter, values)
                    }
                    onDelete={() => handleDeleteFilter(filter)}
                    valueOptions={valueOptions}
                    valueSelected={filter.values}
                  />
                )
              })
          )}

          {edit && !isCustomEconomy ? (
            <ListItemButton
              icon={{ name: 'Add' }}
              onClick={() =>
                setFilters((prev) => {
                  const localId = ShortId.generate()

                  const nextIndex =
                    Math.max(...Object.values(prev).map((item) => item.index)) +
                    1

                  return {
                    ...prev,
                    [localId]: {
                      id: localId,
                      index: nextIndex,
                      attribute_option_id: null,
                      condition: Condition.EQ,
                      values: []
                    }
                  }
                })
              }
              size="small"
              title={t('filters', 'add_filter')}
            />
          ) : null}
        </Box>
      ) : (
        // loading
        <Skeleton variant="rounded" width={'100%'} height={66} />
      )}
    </FormSection>
  )
}

export default FilterSection
